import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useDropdownMenu() {
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const itemsRef = useRef<Set<HTMLElement>>(new Set());
    const lastInteractionWasKeyboard = useRef(false);
    const [open, setOpen] = useState(false);

    // Register individual menu items
    const registerItem = useCallback((el: HTMLElement | null) => {
        if (!el) return;
        itemsRef.current.add(el);
    }, []);

    const closeMenu = useCallback(() => {
        setOpen(false);
    }, []);

    // Detect last interaction type
    useEffect(() => {
        const handlePointer = () => (lastInteractionWasKeyboard.current = false);
        const handleKey = () => (lastInteractionWasKeyboard.current = true);

        globalThis.window.addEventListener('pointerdown', handlePointer);
        globalThis.window.addEventListener('keydown', handleKey);

        return () => {
            globalThis.window.removeEventListener('pointerdown', handlePointer);
            globalThis.window.removeEventListener('keydown', handleKey);
        };
    }, []);

    // Toggle menu
    const toggleMenu = useCallback(() => {
        setOpen((prev) => !prev);

        if (!open) {
            // Clear previous items
            itemsRef.current.clear();
        }
    }, [open]);

    // Focus the first menu item when opened via keyboard
    useLayoutEffect(() => {
        if (!open) return;

        const items = Array.from(itemsRef.current);

        if (lastInteractionWasKeyboard.current) {
            items[0]?.focus();
        } else {
            // Focus menu container so keyboard events are captured
            menuRef.current?.focus();
        }
    }, [open]);

    // Keyboard navigation
    useEffect(() => {
        if (!open || !menuRef.current) return;

        const handleKeys = (ev: KeyboardEvent) => {
            const items = Array.from(itemsRef.current);
            const index = items.indexOf(document.activeElement as HTMLElement);

            switch (ev.key) {
                case 'ArrowDown':
                    ev.preventDefault();
                    items[(index + 1) % items.length]?.focus();
                    break;

                case 'ArrowUp':
                    ev.preventDefault();
                    items[(index - 1 + items.length) % items.length]?.focus();
                    break;

                case 'Home':
                    ev.preventDefault();
                    items[0]?.focus();
                    break;

                case 'End':
                    ev.preventDefault();
                    items[items.length - 1]?.focus();
                    break;

                case 'Escape':
                    closeMenu();
                    // Return focus to trigger after state updates
                    requestAnimationFrame(() => {
                        triggerRef.current?.focus();
                    });
                    break;

                case 'Tab':
                    closeMenu();
                    break;
            }
        };

        const menu = menuRef.current;
        menu.addEventListener('keydown', handleKeys);
        return () => menu.removeEventListener('keydown', handleKeys);
    }, [open, closeMenu]);

    // Click outside to close menu
    useEffect(() => {
        if (!open) return;

        const handleOutside = (ev: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(ev.target as Node) && !triggerRef.current?.contains(ev.target as Node)) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open, closeMenu]);

    return {
        open,
        toggleMenu,
        closeMenu,
        triggerRef,
        menuRef,
        registerItem
    };
}
